import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import FormWrapper from 'components/home/lead-form/wrapper';
import getModelYearList from 'components/home/lead-form/get-model-year-list';
import leadFormSubmissionService from 'components/home/lead-form/lead-form-submission-service';
import ModalBackdrop from 'core/modal-backdrop';
import NameEmailContent from 'components/home/lead-form/form-content/name-email-content';
import PropTypes from 'prop-types';
import SelectBrandModelYearContent from 'components/home/lead-form/form-content/select-brand-model-year-content';
import styles from 'styles/LeadForm.module.scss';
import { useCulture } from 'context/CultureContext';
import { useTranslations } from 'next-intl';

const formSteps = {
    stepOne: 'brand-model-step',
    stepTwo: 'name-email-step',
    stepThree: 'submitted-step'
};

const schema = Yup.object().shape({
    name: Yup.string('error-name-invalid')
        .min(3, 'error-name-invalid')
        .required('error-name-required'),
    lastname: Yup.string('error-lastname-invalid'),
    email: Yup.string('error-email-invalid')
        .email('error-email-invalid')
        .required('error-email-required')
});

const LeadForm = ({ closeHandler, isVisible = null, carList = [] }) => {
    const { culture } = useCulture();
    const [formState, setFormState] = useState({ step: formSteps.stepOne });
    const [error, setError] = useState(null);
    const t = useTranslations('lead-form');
    const longTitle = {
        'brand-model-step': t('model-info'),
        'name-email-step': t('form-info'),
        'submitted-step': t('submitted-info')
    };
    const shortTitle = {
        'brand-model-step': t('model-info-short'),
        'name-email-step': t('form-info-short'),
        'submitted-step': t('submitted-info-short')
    };
    const yearsList = useMemo(() => getModelYearList(), []);
    useEffect(() => {
        if (!isVisible) {
            setFormState({ step: formSteps.stepOne });
            setError(null);
        }
    }, [isVisible]);
    function branModelSubmit ({ brand, year }) {
        setFormState((st) => ({ ...st, brand, year, step: formSteps.stepTwo }));
    }

    function nameEmailSubmit ({ name, lastname, email }) {
        setError(null);
        const payload = {
            name,
            lastname,
            email,
            brand: formState.brand,
            year: formState.year,
            culture: culture
        };

        schema
            .validate({
                email,
                name,
                lastname
            })
            .then((res) => {
                leadFormSubmissionService(payload)
                    .then((data) => {
                        if (data.detail) {
                            setFormState((st) => ({
                                ...st,
                                message: data.detail,
                                step: formSteps.stepThree
                            }));
                        } else {
                            setError(t('error-validation'));
                        }
                    })
                    // eslint-disable-next-line no-console
                    .catch((_error) => {
                        setError(t('error-validation'));
                    });
            })
            .catch((e) => {
                setError(t(e.errors[0]));
            });
    }

    if (!isVisible) {
        return null;
    }

    return (
        <ModalBackdrop closeHandler={closeHandler}>
            <FormWrapper
                title={
                    longTitle[formState.step] || longTitle[formSteps.stepOne]
                }
                shortTitle={
                    shortTitle[formState.step] || shortTitle[formSteps.stepOne]
                }
                errorMsg={error}
            >
                {formState.step === formSteps.stepOne && (
                    <SelectBrandModelYearContent
                        carBrandList={carList}
                        yearsList={yearsList}
                        onSubmit={branModelSubmit}
                    />
                )}
                {formState.step === formSteps.stepTwo && (
                    <NameEmailContent onSubmit={nameEmailSubmit} />
                )}
                {formState.step === formSteps.stepThree && (
                    <div style={{ width: '100%' }}>
                        <div className={styles.submitted_message}>
                            <p>{formState.message}</p>
                        </div>
                    </div>
                )}
            </FormWrapper>
        </ModalBackdrop>
    );
};

LeadForm.propTypes = {
    isVisible: PropTypes.bool,
    closeHandler: PropTypes.func,
    carList: PropTypes.array
};

export default LeadForm;
